import React, { useState, useEffect, useCallback } from 'react';
import { clientService } from '../../services/api';
import { X, Send, Mail, AlertCircle } from 'lucide-react';
import useModal from '../../hooks/useModal';

const SendInvoiceModal = ({ invoice, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  const fetchClientEmail = useCallback(async () => {
    try {
      const response = await clientService.getClient(invoice.client_id);
      const client = response.data;
      if (client.email) {
        setClientEmail(client.email);
        setEmail(client.email); // Set as default
      }
    } catch (error) {
      console.error('Error fetching client email:', error);
    }
  }, [invoice.client_id]);

  useEffect(() => {
    // Fetch client details to get email
    if (invoice.client_id) {
      fetchClientEmail();
    }
  }, [invoice.client_id, fetchClientEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.trim()) {
      setError('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await onSuccess(email.trim());
    } catch (error) {
      console.error('Error sending invoice:', error);
      setError(error.response?.data?.detail || 'Error sending invoice. Please try again.');
      setLoading(false);
    }
  };

  const useClientEmail = () => {
    setEmail(clientEmail);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Send className="w-6 h-6 text-yellow-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Send Invoice</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invoice Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Invoice:</span>
              <span className="font-medium">{invoice.invoice_number}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Client:</span>
              <span className="font-medium">{invoice.client_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Amount:</span>
              <span className="font-medium">{invoice.total_amount ? `$${parseFloat(invoice.total_amount).toFixed(2)}` : '$0.00'}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send To Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="client@example.com"
                  required
                />
              </div>
              
              {/* Client Email Suggestion */}
              {clientEmail && email !== clientEmail && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={useClientEmail}
                    className="text-sm text-yellow-600 hover:text-yellow-700"
                    disabled={loading}
                  >
                    Use client email: {clientEmail}
                  </button>
                </div>
              )}
            </div>

            {/* Preview Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <Send className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                <div className="text-sm text-gray-800">
                  <p className="font-medium mb-1">Invoice will be sent via email</p>
                  <p>The client will receive a professional invoice with payment instructions and your firm's contact information.</p>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center text-red-800">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Footer Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md text-sm font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invoice
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendInvoiceModal;
